import type {
  User,
  Organization,
  Brand,
  Inbox,
  FeedbackEvent,
  FeedbackNote,
  ApiKey,
  Invitation,
  Theme,
  Member,
  PaginatedResponse,
  FeedbackStats,
  SearchResult,
  BillingInfo,
  CheckoutResponse,
  PortalResponse,
  FeedbackPage,
  FeedbackPageResponse,
  FeedbackPageStats,
  PublicFeedbackPage,
  PublicLiveStats,
  LiveSession,
  AIInsights,
  OrganizationDomain,
  DomainVerificationMethod,
  DomainCheckResult,
  DomainAccessRequest,
} from "./types";
import { useAuthStore } from "./store";

// In development, Vite proxies /api to localhost:8787
// In production, use the dedicated API subdomain
const API_BASE = import.meta.env.DEV ? "/api" : "https://api.pollenate.dev";

// Session expired error — lets consumers distinguish from other errors
export class SessionExpiredError extends Error {
  constructor(message = "Session expired. Please log in again.") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

// Mutex for token refresh — prevents concurrent refresh attempts from
// racing and overwriting each other's tokens.
let refreshPromise: Promise<string> | null = null;

// Only call logout if the token that failed is still the current one.
// A concurrent login (e.g. OTP verify) may have already replaced the
// tokens — logging out would destroy the new, valid session.
function scopedLogout(originalToken: string | null): void {
  const { accessToken, refreshToken, logout } = useAuthStore.getState();
  if (accessToken === originalToken || (!accessToken && !refreshToken)) {
    logout();
  }
}

async function refreshAccessToken(
  callerAccessToken: string | null,
): Promise<string> {
  // If a refresh is already in flight, piggyback on it
  if (refreshPromise) return refreshPromise;

  const { refreshToken, setTokens } = useAuthStore.getState();

  if (!refreshToken) {
    scopedLogout(callerAccessToken);
    throw new SessionExpiredError();
  }

  // Capture the refresh token at the start so we can detect concurrent replacements
  const originalRefreshToken = refreshToken;

  refreshPromise = (async () => {
    try {
      const refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: originalRefreshToken }),
      });

      if (!refreshResponse.ok) {
        scopedLogout(callerAccessToken);
        throw new SessionExpiredError();
      }

      const tokens = (await refreshResponse.json()) as {
        accessToken: string;
        refreshToken?: string;
      };
      // Preserve the existing refresh token if the server doesn't return a new one
      setTokens(
        tokens.accessToken,
        tokens.refreshToken ?? originalRefreshToken,
      );
      return tokens.accessToken;
    } catch (error) {
      if (error instanceof SessionExpiredError) throw error;
      scopedLogout(callerAccessToken);
      throw new SessionExpiredError();
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Generic fetch wrapper with auth
async function apiFetch<T>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  // When skipAuth is set, never send Authorization header and never enter
  // the 401→refresh flow.  This is used for public auth endpoints (OTP
  // send/verify, passkey options/verify) so stale tokens in the store
  // cannot interfere with those requests.
  const { skipAuth, ...fetchOptions } = options;
  const initialAccessToken = skipAuth
    ? null
    : useAuthStore.getState().accessToken;

  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");

  if (initialAccessToken) {
    headers.set("Authorization", `Bearer ${initialAccessToken}`);
  }

  let response = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
  });

  // Handle 401 — attempt token refresh, but only if we actually sent a
  // Bearer token.  Public endpoints (e.g. OTP verify) may return 401 for
  // validation errors ("invalid code"), not auth failures.  Treating those
  // as session expirations would mask the real error message.
  if (response.status === 401 && initialAccessToken) {
    try {
      const newAccessToken = await refreshAccessToken(initialAccessToken);

      // Retry original request with the new token
      headers.set("Authorization", `Bearer ${newAccessToken}`);
      response = await fetch(`${API_BASE}${path}`, {
        ...fetchOptions,
        headers,
      });

      // If retried request is still 401, session is truly dead
      if (response.status === 401) {
        scopedLogout(initialAccessToken);
        throw new SessionExpiredError();
      }
    } catch (error) {
      if (error instanceof SessionExpiredError) throw error;
      scopedLogout(initialAccessToken);
      throw new SessionExpiredError();
    }
  }

  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => ({ error: "Unknown error" }))) as { error?: string };
    throw new Error(errorBody.error || `Request failed: ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// ==================== APP VERSION ====================

export interface AppVersionStatus {
  version: string | null;
  build: string | null;
  deploying: boolean;
  releasedAt: string | null;
}

// Deploy status polled by the update badge (public endpoint, no auth)
export async function getAppVersion(): Promise<AppVersionStatus> {
  return apiFetch<AppVersionStatus>("/app/version", { skipAuth: true });
}

// ==================== NOTIFICATIONS ====================

export interface FeedbackActivityItem {
  id: string;
  type: string;
  score: number | null;
  comment: string | null;
  createdAt: string;
  inboxId: string;
  inboxName: string;
  brandName: string;
  orgId: string;
  orgName: string;
}

// Recent feedback across the user's orgs — polled by the desktop app to
// raise native notifications.
export async function getFeedbackActivity(
  since?: string | null,
): Promise<{ items: FeedbackActivityItem[]; latest: string }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : "";
  return apiFetch(`/me/feedback-activity${qs}`);
}

export async function getVapidPublicKey(): Promise<{ publicKey: string }> {
  return apiFetch("/me/push/vapid-public-key");
}

export async function registerPushSubscription(sub: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<{ success: boolean }> {
  return apiFetch("/me/push/subscriptions", {
    method: "POST",
    body: JSON.stringify(sub),
  });
}

export async function unregisterPushSubscription(
  endpoint: string,
): Promise<{ success: boolean }> {
  return apiFetch("/me/push/subscriptions", {
    method: "DELETE",
    body: JSON.stringify({ endpoint }),
  });
}

// ==================== AUTH ====================

export async function sendOtp(email: string): Promise<{ message: string }> {
  return apiFetch("/auth/otp/send", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
  isNewUser?: boolean;
}> {
  return apiFetch("/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ email, code }),
    skipAuth: true,
  });
}

export async function getCurrentUser(): Promise<
  User & { organizations: Organization[] }
> {
  return apiFetch("/auth/me");
}

// ==================== SOCIAL LOGIN (OAUTH) ====================

export type OAuthProvider = "google" | "apple" | "linkedin" | "bluehive";

export interface OAuthProviderAvailability {
  google: boolean;
  apple: boolean;
  linkedin: boolean;
  bluehive: boolean;
}

// Which social providers the server has credentials for (so the UI only shows
// buttons that actually work).
export async function getOAuthProviders(): Promise<OAuthProviderAvailability> {
  return apiFetch("/auth/oauth/providers", { skipAuth: true });
}

// Full-page navigation URL that starts the provider redirect. This is NOT a
// fetch — the browser navigates to it so the provider can show its consent UI.
export function getOAuthStartUrl(
  provider: OAuthProvider,
  params?: { redirect?: string | null; mode?: string | null; client?: "desktop" | null },
): string {
  const qs = new URLSearchParams();
  if (params?.redirect) qs.set("redirect", params.redirect);
  if (params?.mode) qs.set("mode", params.mode);
  if (params?.client) qs.set("client", params.client);
  const query = qs.toString();
  return `${API_BASE}/auth/oauth/${provider}/start${query ? `?${query}` : ""}`;
}

export interface OAuthExchangeResult {
  mfaRequired?: boolean;
  mfaToken?: string;
  accessToken?: string;
  refreshToken?: string;
  user: {
    id: string;
    email: string;
    name?: string | null;
    avatarUrl?: string | null;
  };
  isNewUser?: boolean;
  mode?: string | null;
  returnTo?: string | null;
}

// Redeem the short-lived one-time code (from /auth/callback?code=…) for the
// real session tokens (or an MFA handoff).
export async function exchangeOAuthCode(
  code: string,
): Promise<OAuthExchangeResult> {
  return apiFetch("/auth/oauth/exchange", {
    method: "POST",
    body: JSON.stringify({ code }),
    skipAuth: true,
  });
}

export async function updateProfile(data: {
  name?: string;
  avatarUrl?: string | null;
  timezone?: string | null;
}): Promise<User> {
  return apiFetch("/auth/me", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function logout(): Promise<void> {
  return apiFetch("/auth/logout", { method: "POST" });
}

// ==================== PASSKEYS ====================

export interface PasskeyInfo {
  id: string;
  name: string;
  deviceType: "singleDevice" | "multiDevice";
  backedUp: boolean;
  transports?: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export async function getPasskeyRegistrationOptions(): Promise<
  Record<string, unknown>
> {
  return apiFetch("/auth/passkeys/register/options", { method: "POST" });
}

export async function verifyPasskeyRegistration(
  body: unknown,
): Promise<{ verified: boolean }> {
  return apiFetch("/auth/passkeys/register/verify", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getPasskeyAuthenticationOptions(
  email?: string,
): Promise<Record<string, unknown>> {
  return apiFetch("/auth/passkeys/authenticate/options", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

export async function verifyPasskeyAuthentication(body: unknown): Promise<{
  verified: boolean;
  accessToken: string;
  refreshToken: string;
  user: User & {
    organizations: Array<{
      id: string;
      name: string;
      slug: string;
      role: string;
    }>;
  };
}> {
  return apiFetch("/auth/passkeys/authenticate/verify", {
    method: "POST",
    body: JSON.stringify(body),
    skipAuth: true,
  });
}

export async function listPasskeys(): Promise<{ passkeys: PasskeyInfo[] }> {
  return apiFetch("/auth/passkeys/list");
}

export async function renamePasskey(
  passkeyId: string,
  name: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/auth/passkeys/${passkeyId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function deletePasskey(
  passkeyId: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/auth/passkeys/${passkeyId}`, {
    method: "DELETE",
  });
}

// ==================== TWO-FACTOR AUTHENTICATION (TOTP) ====================

export interface TwoFactorStatus {
  enabled: boolean;
  verified: boolean;
  hasRecoveryCodes: boolean;
  recoveryCodesRemaining: number;
  enabledAt?: string;
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUri: string;
  qrCodeUrl: string;
}

export interface TwoFactorVerifyResult {
  verified: boolean;
  recoveryCodes: string[];
  message: string;
}

export interface MfaVerifyResult {
  verified: boolean;
  accessToken: string;
  refreshToken: string;
  recoveryCodesRemaining?: number;
  user: User & {
    organizations: Array<{
      id: string;
      name: string;
      slug: string;
      role: string;
    }>;
  };
}

export async function getTwoFactorStatus(): Promise<TwoFactorStatus> {
  return apiFetch("/auth/totp/status");
}

export async function setupTwoFactor(): Promise<TwoFactorSetup> {
  const result = await apiFetch<TwoFactorSetup>("/auth/totp/setup", {
    method: "POST",
  });
  // qrCodeUrl is a relative path on the API server; make it absolute so the
  // browser fetches it from the correct origin (api.pollenate.dev, not pollenate.dev).
  if (result.qrCodeUrl && result.qrCodeUrl.startsWith("/")) {
    result.qrCodeUrl = `${API_BASE}${result.qrCodeUrl}`;
  }
  return result;
}

export async function verifyTwoFactorSetup(
  code: string,
): Promise<TwoFactorVerifyResult> {
  return apiFetch("/auth/totp/setup/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function disableTwoFactor(
  code: string,
): Promise<{ success: boolean; message: string }> {
  return apiFetch("/auth/totp/disable", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function regenerateRecoveryCodes(
  code: string,
): Promise<{ recoveryCodes: string[]; message: string }> {
  return apiFetch("/auth/totp/recovery-codes/regenerate", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function verifyMfaCode(
  mfaToken: string,
  code: string,
): Promise<MfaVerifyResult> {
  return apiFetch("/auth/totp/verify", {
    method: "POST",
    body: JSON.stringify({ mfaToken, code }),
    skipAuth: true,
  });
}

export async function verifyMfaRecoveryCode(
  mfaToken: string,
  recoveryCode: string,
): Promise<MfaVerifyResult> {
  return apiFetch("/auth/totp/verify/recovery", {
    method: "POST",
    body: JSON.stringify({ mfaToken, recoveryCode }),
    skipAuth: true,
  });
}

// ==================== ORGANIZATIONS ====================

export async function listOrganizations(): Promise<Organization[]> {
  return apiFetch("/organizations");
}

export async function createOrganization(data: {
  name: string;
  slug?: string;
}): Promise<Organization> {
  return apiFetch("/organizations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getOrganization(
  orgId: string,
): Promise<Organization & { memberCount: number; brandCount: number }> {
  return apiFetch(`/organizations/${orgId}`);
}

export async function updateOrganization(
  orgId: string,
  data: Partial<Organization>,
): Promise<Organization> {
  return apiFetch(`/organizations/${orgId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteOrganization(orgId: string): Promise<void> {
  return apiFetch(`/organizations/${orgId}`, { method: "DELETE" });
}

// ==================== MEMBERS ====================

export async function listOrgMembers(orgId: string): Promise<Member[]> {
  return apiFetch(`/organizations/${orgId}/members`);
}

export async function updateMemberRole(
  orgId: string,
  userId: string,
  role: Exclude<Member["role"], "owner">,
): Promise<Member> {
  return apiFetch(`/organizations/${orgId}/members/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export async function removeMember(
  orgId: string,
  userId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/members/${userId}`, {
    method: "DELETE",
  });
}

// ==================== INVITATIONS ====================

export async function listInvitations(orgId: string): Promise<Invitation[]> {
  return apiFetch(`/organizations/${orgId}/invitations`);
}

export async function createInvitation(
  orgId: string,
  data: {
    email: string;
    role?: Exclude<Invitation["role"], "owner">;
    brandId?: string;
  },
): Promise<Invitation> {
  return apiFetch(`/organizations/${orgId}/invitations`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getInvitation(token: string): Promise<{
  organizationName: string;
  brandName: string | null;
  invitedByName: string;
  role: string;
  email: string;
  expiresAt: string;
  isExpired: boolean;
  isAccepted: boolean;
}> {
  return apiFetch(`/invitations/${token}`);
}

export async function acceptInvitation(token: string): Promise<Organization> {
  return apiFetch(`/invitations/${token}`, { method: "POST" });
}

export async function revokeInvitation(
  orgId: string,
  invitationId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/invitations/${invitationId}`, {
    method: "DELETE",
  });
}

export async function resendInvitation(
  orgId: string,
  invitationId: string,
): Promise<Invitation> {
  return apiFetch(
    `/organizations/${orgId}/invitations/${invitationId}/resend`,
    {
      method: "POST",
    },
  );
}

// ==================== INVITE LINKS ====================

export async function listInviteLinks(
  orgId: string,
): Promise<import("./types").InviteLink[]> {
  return apiFetch(`/organizations/${orgId}/invite-links`);
}

export async function createInviteLink(
  orgId: string,
  data: {
    role?: Exclude<import("./types").OrgRole, "owner">;
    label?: string;
    maxUses?: number;
    expiresIn?: string;
    brandIds?: string[];
  },
): Promise<import("./types").InviteLink> {
  return apiFetch(`/organizations/${orgId}/invite-links`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateInviteLink(
  orgId: string,
  linkId: string,
  data: {
    isActive?: boolean;
    label?: string;
    role?: Exclude<import("./types").OrgRole, "owner">;
    maxUses?: number | null;
    expiresAt?: string | null;
    brandIds?: string[];
  },
): Promise<import("./types").InviteLink> {
  return apiFetch(`/organizations/${orgId}/invite-links/${linkId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteInviteLink(
  orgId: string,
  linkId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/invite-links/${linkId}`, {
    method: "DELETE",
  });
}

export async function getInviteLinkDetails(
  code: string,
): Promise<import("./types").InviteLinkPreview> {
  return apiFetch(`/join/${code}`, { skipAuth: true });
}

export async function acceptInviteLink(
  code: string,
): Promise<import("./types").Organization> {
  return apiFetch(`/join/${code}`, { method: "POST" });
}

// ==================== API KEYS ====================

export async function listApiKeys(
  orgId: string,
  brandId?: string,
): Promise<ApiKey[]> {
  const params = brandId ? `?brandId=${brandId}` : "";
  return apiFetch(`/organizations/${orgId}/api-keys${params}`);
}

export async function createApiKey(
  orgId: string,
  data: {
    name: string;
    brandId?: string;
    scopes?: string[];
    expiresAt?: string;
  },
): Promise<ApiKey> {
  return apiFetch(`/organizations/${orgId}/api-keys`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function revokeApiKey(
  orgId: string,
  keyId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/api-keys/${keyId}`, {
    method: "DELETE",
  });
}

// ==================== OAUTH AUTHORIZATION SERVER (CONNECTED APPS) ====================
// Pollenate acts as an OAuth2 provider: third-party apps (e.g. BlueHive HUM)
// redirect the user to the SPA consent screen, which calls these endpoints.

export interface OAuthConsentClient {
  clientId: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  redirectUris: string[];
  allowedScopes: string[];
}

export interface OAuthConsentInfo {
  client: OAuthConsentClient;
  requestedScopes: Array<{ scope: string; description: string }>;
  organizations: Array<{ id: string; name: string; slug: string; role: string }>;
}

// Validate an authorization request and fetch the details to render on the
// consent screen. `query` is the raw "?client_id=…&redirect_uri=…" string.
export async function getOAuthAuthorizeInfo(query: string): Promise<OAuthConsentInfo> {
  return apiFetch(`/oauth/authorize${query}`);
}

export interface OAuthDecisionResult {
  redirectTo: string;
}

// Approve or deny an authorization request. On success the caller navigates the
// browser to `redirectTo` (which carries the code/state or an error back to the
// third-party app).
export async function submitOAuthDecision(body: {
  clientId: string;
  redirectUri: string;
  scope?: string;
  state?: string;
  codeChallenge?: string;
  codeChallengeMethod?: "S256" | "plain";
  organizationId: string;
  approve: boolean;
}): Promise<OAuthDecisionResult> {
  return apiFetch(`/oauth/authorize/decision`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface ConnectedApp {
  clientId: string;
  clientName: string;
  clientLogoUrl: string | null;
  clientWebsiteUrl: string | null;
  organizationId: string;
  organizationName: string;
  connectedAt: string;
  lastUsedAt: string | null;
}

// Apps the current user has authorized (one row per app + organization).
export async function listConnectedApps(): Promise<ConnectedApp[]> {
  return apiFetch(`/oauth/grants`);
}

// Revoke an app's access. When organizationId is given, only that org's grant
// is revoked; otherwise every grant for the app is revoked.
export async function disconnectApp(
  clientId: string,
  organizationId?: string,
): Promise<void> {
  const qs = organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
  return apiFetch(`/oauth/grants/${encodeURIComponent(clientId)}${qs}`, {
    method: "DELETE",
  });
}

// ==================== WIDGET SETUP ====================

export interface WidgetSetupResponse {
  inbox: Inbox;
  apiKey: ApiKey;
  created: { inbox: boolean; apiKey: boolean };
}

export async function widgetSetup(
  orgId: string,
  brandId: string,
): Promise<WidgetSetupResponse> {
  return apiFetch(`/organizations/${orgId}/widget-setup`, {
    method: "POST",
    body: JSON.stringify({ brandId }),
  });
}

// ==================== BRANDS ====================

export interface ExtractedBrandStyle {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  buttonTextColor: string;
  fontFamily: string;
  borderRadius: number;
  darkPrimaryColor: string | null;
  darkSecondaryColor: string | null;
  darkBackgroundColor: string | null;
  darkTextColor: string | null;
  darkBorderColor: string | null;
  darkButtonTextColor: string | null;
  logoUrl: string | null;
  confidence: "high" | "medium" | "low";
  notes: string;
  sourceUrl: string;
}

export async function extractBrandStyle(
  orgId: string,
  url: string,
): Promise<ExtractedBrandStyle> {
  return apiFetch(`/organizations/${orgId}/brands/extract-style`, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export async function listBrands(orgId: string): Promise<Brand[]> {
  return apiFetch(`/organizations/${orgId}/brands`);
}

export async function createBrand(
  orgId: string,
  data: { name: string; slug?: string; logoUrl?: string },
): Promise<Brand> {
  return apiFetch(`/organizations/${orgId}/brands`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getBrand(
  orgId: string,
  brandId: string,
): Promise<Brand & { memberCount: number; inboxCount: number }> {
  return apiFetch(`/organizations/${orgId}/brands/${brandId}`);
}

export async function updateBrand(
  orgId: string,
  brandId: string,
  data: Partial<Brand>,
): Promise<Brand> {
  return apiFetch(`/organizations/${orgId}/brands/${brandId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function uploadBrandLogo(
  orgId: string,
  brandId: string,
  file: File,
): Promise<{ logoUrl: string }> {
  const formData = new FormData();
  formData.append("logo", file);

  const accessToken = useAuthStore.getState().accessToken;
  const headers: HeadersInit = {};
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const response = await fetch(
    `${API_BASE}/organizations/${orgId}/brands/${brandId}/logo`,
    { method: "POST", headers, body: formData },
  );

  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => ({ error: "Upload failed" }))) as { error?: string };
    throw new Error(errorBody.error || `Upload failed: ${response.status}`);
  }

  return response.json();
}

export async function deleteBrand(
  orgId: string,
  brandId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/brands/${brandId}`, {
    method: "DELETE",
  });
}

// ==================== BRAND MEMBERS ====================

export interface BrandMember {
  id: string;
  userId: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: "admin" | "member";
  joinedAt: string;
}

export async function listBrandMembers(
  orgId: string,
  brandId: string,
): Promise<BrandMember[]> {
  return apiFetch(`/organizations/${orgId}/brands/${brandId}/members`);
}

export async function addBrandMember(
  orgId: string,
  brandId: string,
  userId: string,
  role: "admin" | "member" = "member",
): Promise<{ brandId: string; userId: string; role: string }> {
  return apiFetch(`/organizations/${orgId}/brands/${brandId}/members`, {
    method: "POST",
    body: JSON.stringify({ userId, role }),
  });
}

export async function removeBrandMember(
  orgId: string,
  brandId: string,
  userId: string,
): Promise<void> {
  return apiFetch(
    `/organizations/${orgId}/brands/${brandId}/members/${userId}`,
    { method: "DELETE" },
  );
}

// ==================== INBOXES ====================

export async function listInboxes(
  orgId: string,
  brandId?: string,
): Promise<Inbox[]> {
  const params = brandId ? `?brandId=${brandId}` : "";
  return apiFetch(`/organizations/${orgId}/inboxes${params}`);
}

export async function createInbox(
  orgId: string,
  data: {
    name: string;
    key: string;
    brandId: string;
    description?: string;
    themeId?: string;
  },
): Promise<Inbox> {
  return apiFetch(`/organizations/${orgId}/inboxes`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getInbox(
  orgId: string,
  inboxId: string,
): Promise<
  Inbox & {
    feedbackCount: number;
    notificationRules: Array<{
      id: string;
      type: string;
      target: string;
      events: string[];
      filters: Record<string, unknown>;
      isActive: boolean;
    }>;
  }
> {
  return apiFetch(`/organizations/${orgId}/inboxes/${inboxId}`);
}

export async function updateInbox(
  orgId: string,
  inboxId: string,
  data: Partial<Inbox>,
): Promise<Inbox> {
  return apiFetch(`/organizations/${orgId}/inboxes/${inboxId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteInbox(
  orgId: string,
  inboxId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/inboxes/${inboxId}`, {
    method: "DELETE",
  });
}

// ==================== FEEDBACK ====================

export async function listFeedback(
  orgId: string,
  options?: {
    brandId?: string;
    inboxId?: string;
    type?: string;
    status?: string;
    since?: string;
    until?: string;
    limit?: number;
    cursor?: string;
  },
): Promise<PaginatedResponse<FeedbackEvent>> {
  const params = new URLSearchParams();
  if (options?.brandId) params.set("brandId", options.brandId);
  if (options?.inboxId) params.set("inboxId", options.inboxId);
  if (options?.type) params.set("type", options.type);
  if (options?.status) params.set("status", options.status);
  if (options?.since) params.set("since", options.since);
  if (options?.until) params.set("until", options.until);
  if (options?.limit) params.set("limit", options.limit.toString());
  if (options?.cursor) params.set("cursor", options.cursor);

  const query = params.toString();
  return apiFetch(
    `/organizations/${orgId}/feedback${query ? `?${query}` : ""}`,
  );
}

export async function getFeedback(
  orgId: string,
  feedbackId: string,
): Promise<FeedbackEvent> {
  return apiFetch(`/organizations/${orgId}/feedback/${feedbackId}`);
}

export async function updateFeedback(
  orgId: string,
  feedbackId: string,
  data: {
    status?: string;
    externalTrackerType?: string | null;
    externalTrackerId?: string | null;
    externalTrackerUrl?: string | null;
  },
): Promise<FeedbackEvent> {
  return apiFetch(`/organizations/${orgId}/feedback/${feedbackId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteFeedback(
  orgId: string,
  feedbackId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/feedback/${feedbackId}`, {
    method: "DELETE",
  });
}

// Feedback status counts for Kanban board
export async function getFeedbackStatusCounts(
  orgId: string,
  options?: { brandId?: string; inboxId?: string },
): Promise<Record<string, number>> {
  const params = new URLSearchParams();
  if (options?.brandId) params.set("brandId", options.brandId);
  if (options?.inboxId) params.set("inboxId", options.inboxId);
  const query = params.toString();
  return apiFetch(
    `/organizations/${orgId}/feedback/status-counts${query ? `?${query}` : ""}`,
  );
}

// Send follow-up email to feedback respondent
export async function sendFollowUpEmail(
  orgId: string,
  feedbackId: string,
  data: { message: string; respondentEmail: string },
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/feedback/${feedbackId}/follow-up`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

// Digest preferences
export async function getDigestPreference(
  orgId: string,
): Promise<{ frequency: string; lastSentAt: string | null }> {
  return apiFetch(`/organizations/${orgId}/digest`);
}

export async function updateDigestPreference(
  orgId: string,
  frequency: string,
): Promise<{ frequency: string }> {
  return apiFetch(`/organizations/${orgId}/digest`, {
    method: "PUT",
    body: JSON.stringify({ frequency }),
  });
}

export async function getFeedbackStats(
  orgId: string,
  options?: {
    brandId?: string;
    inboxId?: string;
    type?: string;
    source?: string;
    since?: string;
    until?: string;
    days?: number;
  },
): Promise<FeedbackStats> {
  const params = new URLSearchParams();
  if (options?.brandId) params.set("brandId", options.brandId);
  if (options?.inboxId) params.set("inboxId", options.inboxId);
  if (options?.type) params.set("type", options.type);
  if (options?.source) params.set("source", options.source);
  if (options?.since) params.set("since", options.since);
  if (options?.until) params.set("until", options.until);
  if (options?.days) params.set("days", String(options.days));

  const query = params.toString();
  return apiFetch(
    `/organizations/${orgId}/feedback/stats${query ? `?${query}` : ""}`,
  );
}

export async function getAIInsights(
  orgId: string,
  options?: {
    brandId?: string;
    inboxId?: string;
    days?: number;
    since?: string;
    until?: string;
  },
): Promise<AIInsights> {
  return apiFetch(`/organizations/${orgId}/feedback/insights`, {
    method: "POST",
    body: JSON.stringify({
      brandId: options?.brandId,
      inboxId: options?.inboxId,
      days: options?.days,
      since: options?.since,
      until: options?.until,
    }),
  });
}

// ==================== FEEDBACK NOTES ====================

export async function listFeedbackNotes(
  orgId: string,
  feedbackId: string,
): Promise<{ data: FeedbackNote[] }> {
  return apiFetch(`/organizations/${orgId}/feedback/${feedbackId}/notes`);
}

export async function createFeedbackNote(
  orgId: string,
  feedbackId: string,
  content: string,
): Promise<FeedbackNote> {
  return apiFetch(`/organizations/${orgId}/feedback/${feedbackId}/notes`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}

export async function updateFeedbackNote(
  orgId: string,
  feedbackId: string,
  noteId: string,
  content: string,
): Promise<FeedbackNote> {
  return apiFetch(
    `/organizations/${orgId}/feedback/${feedbackId}/notes/${noteId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ content }),
    },
  );
}

export async function deleteFeedbackNote(
  orgId: string,
  feedbackId: string,
  noteId: string,
): Promise<void> {
  return apiFetch(
    `/organizations/${orgId}/feedback/${feedbackId}/notes/${noteId}`,
    { method: "DELETE" },
  );
}

// ==================== SEARCH ====================

export async function semanticSearch(
  orgId: string,
  query: string,
  options?: {
    brandId?: string;
    inboxId?: string;
    limit?: number;
    threshold?: number;
  },
): Promise<{ results: SearchResult[]; query: string; totalResults: number }> {
  return apiFetch(`/organizations/${orgId}/search`, {
    method: "POST",
    body: JSON.stringify({ query, ...options }),
  });
}

// ==================== THEMES ====================

export async function listPublicThemes(): Promise<Theme[]> {
  return apiFetch("/themes");
}

export async function getTheme(themeId: string): Promise<Theme> {
  return apiFetch(`/themes/${themeId}`);
}

// ==================== NOTIFICATIONS (legacy inbox-scoped) ====================

export async function createNotificationRule(
  orgId: string,
  inboxId: string,
  data: {
    type: "email" | "webhook" | "slack";
    target: string;
    events?: string[];
    filters?: Record<string, unknown>;
  },
): Promise<{
  id: string;
  type: string;
  target: string;
  events: string[];
  isActive: boolean;
}> {
  return apiFetch(`/organizations/${orgId}/inboxes/${inboxId}/notifications`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteNotificationRule(
  orgId: string,
  inboxId: string,
  ruleId: string,
): Promise<void> {
  return apiFetch(
    `/organizations/${orgId}/inboxes/${inboxId}/notifications/${ruleId}`,
    { method: "DELETE" },
  );
}

// ==================== AUTOMATIONS ====================

export async function listAutomations(
  orgId: string,
  inboxId?: string,
  pageId?: string,
): Promise<import("./types").NotificationRule[]> {
  const query = new URLSearchParams();
  if (inboxId) query.set("inboxId", inboxId);
  if (pageId) query.set("pageId", pageId);
  const qs = query.toString();
  return apiFetch(`/organizations/${orgId}/automations${qs ? `?${qs}` : ""}`);
}

export async function getAutomation(
  orgId: string,
  automationId: string,
): Promise<import("./types").NotificationRule> {
  return apiFetch(`/organizations/${orgId}/automations/${automationId}`);
}

export async function createAutomation(
  orgId: string,
  data: {
    name: string;
    inboxId?: string;
    scope?: "inbox" | "organization";
    type: "email" | "webhook" | "slack";
    target: string;
    events?: string[];
    filters?: Record<string, unknown>;
    method?: string;
    headers?: Record<string, string>;
    bodyTemplate?: string | null;
    isActive?: boolean;
  },
): Promise<import("./types").NotificationRule> {
  return apiFetch(`/organizations/${orgId}/automations`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAutomation(
  orgId: string,
  automationId: string,
  data: Partial<{
    name: string;
    type: "email" | "webhook" | "slack";
    target: string;
    events: string[];
    filters: Record<string, unknown>;
    method: string;
    headers: Record<string, string>;
    bodyTemplate: string | null;
    isActive: boolean;
  }>,
): Promise<import("./types").NotificationRule> {
  return apiFetch(`/organizations/${orgId}/automations/${automationId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAutomation(
  orgId: string,
  automationId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/automations/${automationId}`, {
    method: "DELETE",
  });
}

export async function testAutomation(
  orgId: string,
  automationId: string,
): Promise<{
  success: boolean;
  type: string;
  statusCode?: number;
  error?: string;
}> {
  return apiFetch(`/organizations/${orgId}/automations/${automationId}/test`, {
    method: "POST",
  });
}

export async function getAutomationLogs(
  orgId: string,
  automationId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<
  import("./types").PaginatedResponse<import("./types").AutomationLog>
> {
  const params = new URLSearchParams();
  if (options.limit) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(
    `/organizations/${orgId}/automations/${automationId}/logs${qs}`,
  );
}

export async function getAutomationAuditLogs(
  orgId: string,
  options: {
    limit?: number;
    cursor?: string;
    status?: string;
    event?: string;
  } = {},
): Promise<{
  items: import("./types").AuditLogEntry[];
  cursor: string | null;
  hasMore: boolean;
}> {
  const params = new URLSearchParams();
  if (options.limit) params.set("limit", String(options.limit));
  if (options.cursor) params.set("cursor", options.cursor);
  if (options.status) params.set("status", options.status);
  if (options.event) params.set("event", options.event);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`/organizations/${orgId}/automations/logs/all${qs}`);
}

// ==================== SECRETS ====================

export async function listSecrets(
  orgId: string,
): Promise<import("./types").OrgSecret[]> {
  return apiFetch(`/organizations/${orgId}/secrets`);
}

export async function createSecret(
  orgId: string,
  data: { name: string; value: string; description?: string },
): Promise<import("./types").OrgSecret> {
  return apiFetch(`/organizations/${orgId}/secrets`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateSecret(
  orgId: string,
  secretId: string,
  data: { value?: string; description?: string | null },
): Promise<import("./types").OrgSecret> {
  return apiFetch(`/organizations/${orgId}/secrets/${secretId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteSecret(
  orgId: string,
  secretId: string,
): Promise<void> {
  return apiFetch(`/organizations/${orgId}/secrets/${secretId}`, {
    method: "DELETE",
  });
}

// ==================== ANALYTICS ====================

export async function getInboxImpressions(
  inboxId: string,
  options: {
    days?: number;
    since?: string;
    until?: string;
    groupBy?: "hour" | "day" | "week";
  } = {},
): Promise<import("./types").ImpressionAnalytics> {
  const params = new URLSearchParams();
  if (options.days) params.set("days", String(options.days));
  if (options.since) params.set("since", options.since);
  if (options.until) params.set("until", options.until);
  if (options.groupBy) params.set("groupBy", options.groupBy);
  const query = params.toString();
  const response = await apiFetch<{
    summary: {
      totalImpressions: number;
      impressionsWithFeedback: number;
      conversionRate: number;
      uniqueUsers: number;
      uniqueSessions: number;
    };
    timeSeries: import("./types").ImpressionAnalytics["timeSeries"];
    topPages: Array<{
      page_url: string;
      impressions: number;
      with_feedback: number;
      conversion_rate: number;
    }>;
    topUsers: Array<{
      external_user_id: string;
      impressions: number;
      feedback_count: number;
      first_seen: string;
      last_seen: string;
    }>;
  }>(`/analytics/inboxes/${inboxId}/impressions${query ? `?${query}` : ""}`);

  // Map backend snake_case response to frontend camelCase types
  return {
    totalImpressions: response.summary.totalImpressions,
    impressionsWithFeedback: response.summary.impressionsWithFeedback,
    conversionRate: response.summary.conversionRate,
    uniqueUsers: response.summary.uniqueUsers,
    uniqueSessions: response.summary.uniqueSessions,
    timeSeries: response.timeSeries,
    topPages: response.topPages.map((p) => ({
      pageUrl: p.page_url,
      impressions: p.impressions,
      withFeedback: p.with_feedback,
      conversionRate: p.conversion_rate,
    })),
    topUsers: response.topUsers.map((u) => ({
      externalUserId: u.external_user_id,
      impressions: u.impressions,
      feedbackCount: u.feedback_count,
      firstSeen: u.first_seen,
      lastSeen: u.last_seen,
    })),
  };
}

// ==================== BILLING ====================

export async function getBillingInfo(orgId: string): Promise<BillingInfo> {
  return apiFetch(`/organizations/${orgId}/billing`);
}

export async function createCheckoutSession(
  orgId: string,
  plan: "growth",
): Promise<CheckoutResponse> {
  return apiFetch(`/organizations/${orgId}/billing/checkout`, {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

export async function createBillingPortalSession(
  orgId: string,
): Promise<PortalResponse> {
  return apiFetch(`/organizations/${orgId}/billing/portal`, {
    method: "POST",
  });
}

export async function cancelSubscription(
  orgId: string,
  immediately = false,
): Promise<{
  message: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
}> {
  return apiFetch(`/organizations/${orgId}/billing/cancel`, {
    method: "POST",
    body: JSON.stringify({ immediately }),
  });
}

export async function getUsage(
  orgId: string,
): Promise<import("./types").UsageInfo> {
  return apiFetch(`/organizations/${orgId}/usage`);
}

// ==================== ADMIN ====================

export async function getAdminStats(): Promise<import("./types").AdminStats> {
  return apiFetch("/admin/stats");
}

export async function getAdminOrganizations(params?: {
  search?: string;
  plan?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<{
  organizations: import("./types").AdminOrganization[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.plan) searchParams.set("plan", params.plan);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  const qs = searchParams.toString();
  return apiFetch(`/admin/organizations${qs ? `?${qs}` : ""}`);
}

export async function getAdminOrganizationDetail(
  orgId: string,
): Promise<import("./types").AdminOrganizationDetail> {
  return apiFetch(`/admin/organizations/${orgId}`);
}

export async function updateOrganizationPlan(
  orgId: string,
  data: {
    planName: "trial" | "starter" | "growth" | "enterprise";
    subscriptionStatus?: "trial" | "active" | "past_due" | "canceled";
    currentPeriodEnd?: string;
    adminNotes?: string;
  },
): Promise<{
  success: boolean;
  organization: { id: string; planName: string; subscriptionStatus: string };
}> {
  return apiFetch(`/admin/organizations/${orgId}/plan`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function updateOrganizationNotes(
  orgId: string,
  notes: string | null,
): Promise<{ success: boolean }> {
  return apiFetch(`/admin/organizations/${orgId}/notes`, {
    method: "PATCH",
    body: JSON.stringify({ notes }),
  });
}

export async function getAdminUsers(params?: {
  search?: string;
  adminOnly?: boolean;
  page?: number;
  limit?: number;
}): Promise<{
  users: import("./types").AdminUser[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.adminOnly) searchParams.set("adminOnly", "true");
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  const qs = searchParams.toString();
  return apiFetch(`/admin/users${qs ? `?${qs}` : ""}`);
}

export async function toggleUserAdmin(
  userId: string,
  isSiteAdmin: boolean,
): Promise<{ success: boolean }> {
  return apiFetch(`/admin/users/${userId}/admin`, {
    method: "PATCH",
    body: JSON.stringify({ isSiteAdmin }),
  });
}

export async function createSiteAdmin(
  email: string,
  name?: string,
): Promise<{
  success: boolean;
  created: boolean;
  user: {
    id: string;
    email: string;
    name: string | null;
    isSiteAdmin: boolean;
  };
}> {
  return apiFetch("/admin/users", {
    method: "POST",
    body: JSON.stringify({ email, name }),
  });
}

// ==================== ADMIN DOMAINS ====================

export async function adminListOrgDomains(
  orgId: string,
): Promise<import("./types").OrganizationDomain[]> {
  return apiFetch(`/admin/organizations/${orgId}/domains`);
}

export async function adminAddOrgDomain(
  orgId: string,
  domain: string,
  method: import("./types").DomainVerificationMethod = "dns_txt",
): Promise<import("./types").OrganizationDomain> {
  return apiFetch(`/admin/organizations/${orgId}/domains`, {
    method: "POST",
    body: JSON.stringify({ domain, method }),
  });
}

export async function adminForceVerifyDomain(
  orgId: string,
  domainId: string,
): Promise<{ success: boolean; verifiedAt: string; verifiedBy: { id: string; name: string | null; email: string } | null }> {
  return apiFetch(
    `/admin/organizations/${orgId}/domains/${domainId}/force-verify`,
    { method: "POST" },
  );
}

export async function adminDeleteOrgDomain(
  orgId: string,
  domainId: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/admin/organizations/${orgId}/domains/${domainId}`, {
    method: "DELETE",
  });
}

// ==================== ADMIN ORG MANAGEMENT ====================

export async function createAdminOrganization(data: {
  name: string;
  slug?: string;
  ownerEmail?: string;
  planName?: "trial" | "starter" | "growth" | "enterprise";
}): Promise<{
  success: boolean;
  organization: {
    id: string;
    name: string;
    slug: string;
    planName: string;
    subscriptionStatus: string;
    createdAt: string;
  };
  owner: { id: string; email: string; name: string | null } | null;
}> {
  return apiFetch("/admin/organizations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminOrganization(
  orgId: string,
  data: { name?: string; slug?: string },
): Promise<{
  success: boolean;
  organization: { id: string; name: string; slug: string; updatedAt: string };
}> {
  return apiFetch(`/admin/organizations/${orgId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function archiveOrganization(
  orgId: string,
  archived: boolean,
): Promise<{ success: boolean; organizationId: string; archivedAt: string | null }> {
  return apiFetch(`/admin/organizations/${orgId}/archive`, {
    method: "PATCH",
    body: JSON.stringify({ archived }),
  });
}

// ==================== PERMISSIONS ====================

export interface RoleInfo {
  role: string;
  label: string;
  description: string;
  permissions: string[];
  invitable: boolean;
}

export interface PermissionInfo {
  key: string;
  description: string;
}

export interface RolesResponse {
  roles: RoleInfo[];
  allPermissions: PermissionInfo[];
}

export interface MemberPermissions {
  role: string;
  roleLabel?: string;
  defaultPermissions: string[];
  overrides: Array<{ permission: string; granted: boolean }>;
  effectivePermissions: string[];
}

export async function getRoles(orgId: string): Promise<RolesResponse> {
  return apiFetch(`/organizations/${orgId}/permissions/roles`);
}

export async function getMemberPermissions(
  orgId: string,
  userId: string,
): Promise<MemberPermissions> {
  return apiFetch(`/organizations/${orgId}/permissions/members/${userId}`);
}

export async function setMemberPermissionOverrides(
  orgId: string,
  userId: string,
  overrides: Array<{ permission: string; granted: boolean | null }>,
): Promise<MemberPermissions> {
  return apiFetch(`/organizations/${orgId}/permissions/members/${userId}`, {
    method: "PUT",
    body: JSON.stringify({ overrides }),
  });
}

// ==================== FEEDBACK PAGES ====================

export interface AIGeneratedFeedbackForm {
  title: string;
  description: string | null;
  questions: Array<{
    type: string;
    prompt: string;
    description: string | null;
    required: boolean;
    sortOrder: number;
    config: { options: string[] | null; multiline: boolean | null };
  }>;
}

export async function generateFeedbackPageWithAI(
  orgId: string,
  data: {
    prompt: string;
    existingQuestions?: Array<{
      type: string;
      prompt: string;
      description?: string | null;
      required?: boolean;
      config?: Record<string, unknown>;
    }>;
  },
  signal?: AbortSignal,
): Promise<AIGeneratedFeedbackForm> {
  // Always apply a 60-second timeout, combining with any caller signal
  const timeoutMs = 60_000;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  // Combine caller signal + timeout signal
  let combinedSignal: AbortSignal;
  if (signal) {
    if (typeof AbortSignal.any === "function") {
      combinedSignal = AbortSignal.any([signal, timeoutController.signal]);
    } else {
      // Fallback for environments without AbortSignal.any
      const linked = new AbortController();
      const onAbort = () => linked.abort();
      signal.addEventListener("abort", onAbort);
      timeoutController.signal.addEventListener("abort", onAbort);
      combinedSignal = linked.signal;
    }
  } else {
    combinedSignal = timeoutController.signal;
  }

  try {
    return await apiFetch(
      `/organizations/${orgId}/feedback-pages/ai/generate`,
      {
        method: "POST",
        body: JSON.stringify(data),
        signal: combinedSignal,
      },
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Distinguish user cancellation from timeout
      if (signal?.aborted) throw err;
      throw new Error("AI generation timed out. Please try again.");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface ImportedGoogleFormQuestion {
  type: string;
  prompt: string;
  description: string | null;
  required: boolean;
  sortOrder: number;
  config: Record<string, unknown>;
}

export interface ImportedGoogleForm {
  title: string;
  description: string | null;
  questions: ImportedGoogleFormQuestion[];
  settings: {
    thankYouMessage?: string;
    requireContactInfo?: boolean;
    singleSubmission?: boolean;
  };
  skipped: string[];
}

export async function importGoogleFormsPage(
  orgId: string,
  url: string,
  signal?: AbortSignal,
): Promise<ImportedGoogleForm> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/import/google-forms`,
    {
      method: "POST",
      body: JSON.stringify({ url }),
      signal,
    },
  );
}

export async function listFeedbackPages(
  orgId: string,
  params?: { status?: string; brandId?: string },
): Promise<FeedbackPage[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set("status", params.status);
  if (params?.brandId) searchParams.set("brandId", params.brandId);
  const qs = searchParams.toString();
  return apiFetch(
    `/organizations/${orgId}/feedback-pages${qs ? `?${qs}` : ""}`,
  );
}

export async function getFeedbackPage(
  orgId: string,
  pageId: string,
): Promise<FeedbackPage> {
  return apiFetch(`/organizations/${orgId}/feedback-pages/${pageId}`);
}

export async function createFeedbackPage(
  orgId: string,
  data: {
    title: string;
    slug?: string;
    description?: string | null;
    brandId?: string | null;
    accentColor?: string;
    backgroundColor?: string;
    logoUrl?: string | null;
    thankYouMessage?: string;
    thankYouUrl?: string | null;
    requireContactInfo?: boolean;
    allowAnonymous?: boolean;
    singleSubmission?: boolean;
    liveResultsEnabled?: boolean;
    displayMode?: "all" | "one_at_a_time";
    introMarkdown?: string | null;
    status?: string;
    sharedAcrossBrands?: boolean;
    questions?: Array<{
      type: string;
      prompt: string;
      description?: string | null;
      required?: boolean;
      sortOrder?: number;
      config?: Record<string, unknown>;
    }>;
  },
): Promise<{ id: string; slug: string }> {
  return apiFetch(`/organizations/${orgId}/feedback-pages`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateFeedbackPage(
  orgId: string,
  pageId: string,
  data: Partial<{
    title: string;
    slug: string;
    description: string | null;
    brandId: string | null;
    accentColor: string;
    backgroundColor: string;
    logoUrl: string | null;
    thankYouMessage: string;
    thankYouUrl: string | null;
    requireContactInfo: boolean;
    allowAnonymous: boolean;
    singleSubmission: boolean;
    liveResultsEnabled: boolean;
    displayMode: "all" | "one_at_a_time";
    introMarkdown: string | null;
    submitButtonText: string;
    status: string;
    sharedAcrossBrands: boolean;
  }>,
): Promise<{ success: boolean }> {
  return apiFetch(`/organizations/${orgId}/feedback-pages/${pageId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function cloneFeedbackPage(
  orgId: string,
  pageId: string,
  data: {
    title: string;
    brandId: string;
    overrides?: {
      description?: string | null;
      status?: string;
      accentColor?: string;
      backgroundColor?: string;
      thankYouMessage?: string;
      thankYouUrl?: string | null;
      requireContactInfo?: boolean;
      allowAnonymous?: boolean;
      singleSubmission?: boolean;
      liveResultsEnabled?: boolean;
      displayMode?: "all" | "one_at_a_time";
      introMarkdown?: string | null;
      submitButtonText?: string;
    };
  },
): Promise<{ id: string; slug: string; shortCode: string }> {
  return apiFetch(`/organizations/${orgId}/feedback-pages/${pageId}/clone`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteFeedbackPage(
  orgId: string,
  pageId: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/organizations/${orgId}/feedback-pages/${pageId}`, {
    method: "DELETE",
  });
}

export async function addFeedbackPageQuestion(
  orgId: string,
  pageId: string,
  data: {
    type: string;
    prompt: string;
    description?: string | null;
    required?: boolean;
    sortOrder?: number;
    config?: Record<string, unknown>;
  },
): Promise<{ id: string }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/questions`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

export async function updateFeedbackPageQuestion(
  orgId: string,
  pageId: string,
  questionId: string,
  data: Partial<{
    type: string;
    prompt: string;
    description: string | null;
    required: boolean;
    sortOrder: number;
    config: Record<string, unknown>;
  }>,
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/questions/${questionId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function deleteFeedbackPageQuestion(
  orgId: string,
  pageId: string,
  questionId: string,
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/questions/${questionId}`,
    {
      method: "DELETE",
    },
  );
}

export async function reorderFeedbackPageQuestions(
  orgId: string,
  pageId: string,
  questionIds: string[],
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/questions-order`,
    {
      method: "PUT",
      body: JSON.stringify({ questionIds }),
    },
  );
}

export async function replaceAllFeedbackPageQuestions(
  orgId: string,
  pageId: string,
  data: {
    questions: Array<{
      type: string;
      prompt: string;
      description?: string | null;
      required?: boolean;
      sortOrder?: number;
      config?: Record<string, unknown>;
    }>;
    title?: string;
    description?: string | null;
  },
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/questions-replace`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function getFeedbackPageResponses(
  orgId: string,
  pageId: string,
  params?: { limit?: number; cursor?: string },
): Promise<PaginatedResponse<FeedbackPageResponse>> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.cursor) searchParams.set("cursor", params.cursor);
  const qs = searchParams.toString();
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/responses${qs ? `?${qs}` : ""}`,
  );
}

export async function getFeedbackPageStats(
  orgId: string,
  pageId: string,
): Promise<FeedbackPageStats> {
  return apiFetch(`/organizations/${orgId}/feedback-pages/${pageId}/stats`);
}

// Public feedback page (no auth required)
export async function getPublicFeedbackPage(
  orgSlug: string,
  pageSlug: string,
  options?: { brand?: string },
): Promise<PublicFeedbackPage> {
  const query = options?.brand
    ? `?brand=${encodeURIComponent(options.brand)}`
    : "";
  const response = await fetch(`${API_BASE}/p/${orgSlug}/${pageSlug}${query}`);
  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => ({ error: "Page not found" }))) as { error?: string };
    throw new Error(errorBody.error || "Page not found");
  }
  return response.json();
}

/** Build the public URL for a media object stored in R2 (e.g. feedback page file uploads) */
export function publicMediaUrl(key: string): string {
  return `${API_BASE}/media/${key}`;
}

export interface UploadedFeedbackFile {
  key: string;
  name: string;
  size: number;
  contentType: string;
}

/**
 * Upload a file attachment for a file_upload/voice question on a public
 * feedback page. Uses XHR so upload progress can be reported.
 */
export function uploadPublicFeedbackPageFile(
  orgSlug: string,
  pageSlug: string,
  questionId: string,
  file: File | Blob,
  fileName: string,
  onProgress?: (percent: number) => void,
): Promise<UploadedFeedbackFile> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("questionId", questionId);
    formData.append("file", file, fileName);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/p/${orgSlug}/${pageSlug}/upload`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadedFeedbackFile);
        } catch {
          reject(new Error("Upload failed"));
        }
      } else {
        let message = "Upload failed";
        try {
          const body = JSON.parse(xhr.responseText) as { error?: string };
          if (body.error) message = body.error;
        } catch {
          // ignore parse errors
        }
        reject(new Error(message));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(formData);
  });
}

export async function submitPublicFeedbackPage(
  orgSlug: string,
  pageSlug: string,
  data: {
    respondentName?: string | null;
    respondentEmail?: string | null;
    turnstileToken: string | null;
    answers: Array<{
      questionId: string;
      score?: number | null;
      textValue?: string | null;
      multiValues?: string[] | null;
    }>;
  },
): Promise<{
  success: boolean;
  responseId: string;
  thankYouMessage: string;
  thankYouUrl: string | null;
}> {
  const response = await fetch(`${API_BASE}/p/${orgSlug}/${pageSlug}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => ({ error: "Submission failed" }))) as { error?: string };
    throw new Error(errorBody.error || "Submission failed");
  }
  return response.json();
}

// ==================== LIVE POLLING RESULTS ====================

export async function getPublicLiveStats(
  orgSlug: string,
  pageSlug: string,
): Promise<{ stats: PublicLiveStats; submissionsOpen: boolean }> {
  const response = await fetch(
    `${API_BASE}/p/${orgSlug}/${pageSlug}/live-stats`,
  );
  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => ({ error: "Live results unavailable" }))) as {
      error?: string;
    };
    throw new Error(errorBody.error || "Live results unavailable");
  }
  return response.json();
}

/** Build the WebSocket URL for the live-results stream of a public page. */
export function liveResultsWsUrl(orgSlug: string, pageSlug: string): string {
  // In dev API_BASE is the relative "/api" (proxied to :8787). WebSockets need
  // an absolute URL, so connect straight to the worker dev port. In prod
  // API_BASE is the absolute API origin; just swap the protocol to ws(s).
  const httpBase = API_BASE.startsWith("http")
    ? API_BASE
    : `${window.location.protocol}//${window.location.hostname}:8787`;
  const wsBase = httpBase.replace(/^http/, "ws");
  return `${wsBase}/p/${encodeURIComponent(orgSlug)}/${encodeURIComponent(
    pageSlug,
  )}/live`;
}

export async function setLiveTimer(
  orgId: string,
  pageId: string,
  action: "start" | "stop" | "reopen" | "close",
  durationSec?: number,
): Promise<{ success: boolean; session: LiveSession }> {
  return apiFetch(
    `/organizations/${orgId}/feedback-pages/${pageId}/live/timer`,
    {
      method: "POST",
      body: JSON.stringify({ action, durationSec }),
    },
  );
}

// ==================== DOMAIN VERIFICATION ====================

export async function checkEmailDomain(): Promise<DomainCheckResult> {
  return apiFetch("/auth/domain-check");
}

export async function listOrganizationDomains(
  orgId: string,
): Promise<OrganizationDomain[]> {
  return apiFetch(`/organizations/${orgId}/domains`);
}

export async function addOrganizationDomain(
  orgId: string,
  data: { domain: string; method: DomainVerificationMethod },
): Promise<
  OrganizationDomain & {
    instructions: { method: string; steps: string[]; value: string };
  }
> {
  return apiFetch(`/organizations/${orgId}/domains`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function verifyOrganizationDomain(
  orgId: string,
  domainId: string,
): Promise<{ verified: boolean; verifiedAt?: string; error?: string; detail?: string }> {
  return apiFetch(`/organizations/${orgId}/domains/${domainId}/verify`, {
    method: "POST",
  });
}

export async function deleteOrganizationDomain(
  orgId: string,
  domainId: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/organizations/${orgId}/domains/${domainId}`, {
    method: "DELETE",
  });
}

// ==================== ACCESS REQUESTS ====================

export async function submitAccessRequest(data: {
  organizationId: string;
  message?: string;
}): Promise<{ id: string; status: string; message: string }> {
  return apiFetch("/access-requests", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function listMyAccessRequests(): Promise<
  Array<{
    id: string;
    organizationId: string;
    organizationName: string;
    organizationSlug: string;
    domain: string;
    status: string;
    message: string | null;
    createdAt: string;
  }>
> {
  return apiFetch("/access-requests/mine");
}

export async function listOrgAccessRequests(
  orgId: string,
): Promise<DomainAccessRequest[]> {
  return apiFetch(`/organizations/${orgId}/access-requests`);
}

export async function approveAccessRequest(
  orgId: string,
  requestId: string,
  role?: string,
): Promise<{ success: boolean; role: string }> {
  return apiFetch(
    `/organizations/${orgId}/access-requests/${requestId}/approve`,
    {
      method: "POST",
      body: JSON.stringify({ role }),
    },
  );
}

export async function denyAccessRequest(
  orgId: string,
  requestId: string,
): Promise<{ success: boolean }> {
  return apiFetch(
    `/organizations/${orgId}/access-requests/${requestId}/deny`,
    {
      method: "POST",
    },
  );
}
